import React, { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState, } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate ,useLocation} from "react-router-dom";
import server from "../server";
import { AuditInfo } from "../domains/models/common/audit-info";
import type { RoleList, UserRole } from "../domains/models/user";
import { User } from "../domains/models/user";

export interface UserLoginInterface {
    loginStatus?: boolean;
    auditInfo?: AuditInfo;
    id: string;
    email: string;
    name: {
        firstName: string;
        middleName?: string;
        lastName: string;
    };
    roles: RoleList;
    isAdmin: boolean;
    employeeId?: string;
    profileURL?: string;
}

export interface UserContextType {
    user: UserLoginInterface;
    setUser: Dispatch<SetStateAction<UserLoginInterface>>;
    tokenToUserData: () => void;
}


const defaultUser: UserLoginInterface = {
    loginStatus: false,
    id: "",
    email: "",
    name: {
        firstName: "",
        middleName: "",
        lastName: "",
    },
    roles: [],
    isAdmin: false,
    profileURL: "",
};


const UserLoginContext = createContext<UserContextType>({
    user: defaultUser,
    setUser: () => {
    },
    tokenToUserData: () => {
    },
});


export const UserLoginContextProvider: React.FC<{
    children: ReactNode;
}> = ({children}) => {

    const [user, setUser] = useState<UserLoginInterface>(defaultUser);

    const clearAuthData = () => {
        localStorage.removeItem("id");
        localStorage.removeItem(`${process.env.REACT_APP_REFRESH_TOKEN_KEY}`);
    };



    const tokenToUserData = useCallback(async () => {
        if (window.location.pathname.includes("forgot-password")) return;

        const storedToken = localStorage.getItem(`${process.env.REACT_APP_REFRESH_TOKEN_KEY}`);

        if (!storedToken) {
            clearAuthData();
            setUser(defaultUser);
            return;
        }

        try {
            const parsedToken = JSON.parse(storedToken);

            if (!parsedToken.exp || parsedToken.exp < Date.now()) {
                clearAuthData();
                setUser(defaultUser);
                return;
            }

            const decoded: any = jwtDecode(parsedToken.token);

            const user: User = await server.user.getByEmail(decoded.sub);

            if (!user.id) {
                clearAuthData();
                setUser(defaultUser);
                return;
            }

            let profileURL = '';

            if (user.employeeId) {
                profileURL = await fetchProfilePhoto(user.employeeId);
            }

            const userCardData: UserLoginInterface = {
                ...user,
                name: {
                    firstName: user.name.firstName ?? '',
                    lastName: user.name.lastName ?? ''
                },
                id: user.id,
                email: user.email,
                ...(user.employeeId && {employeeId: user.employeeId}),
                loginStatus: true,
                profileURL,
                isAdmin: user.roles.includes("ADMIN" as UserRole),
                roles: user.roles,
            };

            setUser(userCardData);

        } catch {
            clearAuthData();
            setUser(defaultUser);
        }
    }, []);


    const fetchProfilePhoto = async (employeeId?: string): Promise<string> => {
        if (!employeeId) return '';
        try {
            const params = {employeeId: employeeId, documentType: 'PROFILE_PHOTO', purpose: 'PROFILE_PHOTO'}
            const docs = await server.document.find(
                params,
                0
            );
            if (!docs || docs.length === 0) {
                return '';
            }
            const doc = docs[0];
            try {
                const file = await server.document.download(doc.id);
                return URL.createObjectURL(file);
            } catch (error) {
                console.error('Failed to download profile photo:', error);
                return '';
            }
        } catch (error) {
            console.error('Failed to fetch profile photo :', error);
            return '';
        }
    };

    useEffect(() => {
        tokenToUserData();
    }, []);

    useEffect(() => {
        const handleLogout = () => {
            console.log('LOGOUT OCCURED')
            clearAuthData();
            setUser(defaultUser);
        };

        window.addEventListener("logout", handleLogout);
        return () => window.removeEventListener("logout", handleLogout);
    }, []);


    return (
        <UserLoginContext.Provider value={{user, setUser, tokenToUserData}}>
            {children}
        </UserLoginContext.Provider>
    );
};

export default UserLoginContext;
